import {
    ColDef,
    ColGroupDef,
    ITooltipParams,
    ValueFormatterFunc,
    ValueFormatterParams
} from "ag-grid-community";
import { ConstraintType } from "in_queue/types/scenarioResultType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { formatCosts, formatPower } from "in_queue/utils/formatting";
import { capitalize, startCase, toLower } from "lodash";
export interface NiraColDef<TData, TColumn extends string>
    extends ColDef<TData> {
    colId?: TColumn;
}

export interface NiraColGroupDef<TData, TColumn extends string>
    extends ColGroupDef<TData> {
    colId?: TColumn;
    children: (NiraColDef<TData, TColumn> | NiraColGroupDef<TData, TColumn>)[];
}

export type AgGridColumnDefs<TData, TColumn extends string> = (
    | NiraColDef<TData, TColumn>
    | NiraColGroupDef<TData, TColumn>
)[];

export const percentValueFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, number>
) => (params.value ? `${params.value.toFixed(1)}%` : "-");

export const dfaxFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, number>
) => (params.value != null ? `${(params.value * 100).toFixed(1)}%` : "-");

export const currencyFormatterHelper = (
    value: number | null | undefined
): string =>
    (value as unknown as string) === "Infinity"
        ? "-"
        : value != null
        ? formatCosts(value)
        : "-";

export const currencyFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, number>
) =>
    // ag-grid converts JS numeric Infinity into a string "Infinity" for some reason
    currencyFormatterHelper(params.value);

export const powerFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, number>
) => (params.value != null ? formatPower(params.value) : "-");

export const roundingFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, number>
) => {
    return (
        params.value?.toLocaleString(undefined, {
            useGrouping: false,
            maximumFractionDigits: 2
        }) ?? "-"
    );
};

export const titleCaseStringFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, string>
) => (params.value ? capitalize(params.value) : "-");

export const stringFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, string>
) => params.value ?? "-";

export const presenceFormatter: ValueFormatterFunc = (params) =>
    params.value === undefined || params.value === null ? "No" : "Yes";

// AG Grid's default comparator sorts nulls first. Unranked projects belong at
// the bottom in either direction.
export const compareNullsLast = (
    a: number | null,
    b: number | null,
    _nodeA: unknown,
    _nodeB: unknown,
    isDescending: boolean
): number => {
    if (a === null && b === null) return 0;
    if (a === null) return isDescending ? -1 : 1;
    if (b === null) return isDescending ? 1 : -1;
    return a - b;
};

// Show the full cell text on hover so content that is silently clipped
// by the line-clamp rule in NiraAgGrid.scss remains accessible.
export const cellTooltipValueGetter = (
    params: ITooltipParams
): string | undefined => {
    if (params.valueFormatted != null) {
        return params.valueFormatted;
    }
    if (params.value == null) {
        return undefined;
    }
    return typeof params.value === "string"
        ? params.value
        : String(params.value);
};

/**
 * Shared `defaultColDef` for the Scenario Results tables (Assigned
 * upgrades / Constraints / Projects). Defined as a single module-level
 * constant — NOT an inline object literal at the call site — so its
 * reference is stable across renders. ag-grid-react diffs grid options by
 * reference every render; a fresh `defaultColDef` object makes AG Grid
 * re-derive all columns (ColumnModel.onSharedColDefChanged), which resets
 * the user's column order (`maintainColumnOrder` defaults to false) and
 * resized widths back to the column defs on every re-render (e.g. a tab
 * switch). A stable reference avoids that re-derivation entirely.
 */
export const SCENARIO_TABLE_DEFAULT_COL_DEF: ColDef = {
    useValueFormatterForExport: false,
    wrapText: true,
    cellStyle: { display: "flex", alignItems: "center" },
    tooltipValueGetter: cellTooltipValueGetter
};

export const timingFormatter: ValueFormatterFunc<ScenarioMetadata, string> = (
    params
) => {
    const scenario = params.data;
    if (!scenario) {
        return "-";
    }

    if (!params.value) {
        return "-";
    }

    return new Date(params.value).toLocaleString();
};

/**
 * These constraint types will be formatted in sentence case (e.g. "Summer Peak").
 * The rest will fall back to all-caps.
 */
const sentenceCaseConstraintTypes: Set<ConstraintType> = new Set([
    ConstraintType.SummerPeak,
    ConstraintType.LightLoad
]);
export const constraintTypeFormatter: ValueFormatterFunc = <T>(
    params: ValueFormatterParams<T, ConstraintType>
) => {
    const constraintType = params.value;
    if (constraintType == null) {
        return "";
    } else if (constraintType === ConstraintType.SharedNetworkUpgrade) {
        return "SNU";
    }
    if (sentenceCaseConstraintTypes.has(constraintType)) {
        return startCase(toLower(constraintType.replace("_", " ")));
    } else {
        return constraintType.replace("_", " ");
    }
};

// Custom type guard to determine if a column definition is a ColDef
export const isColDef = <TData, TColumn extends string>(
    columnDef: NiraColDef<TData, TColumn> | NiraColGroupDef<TData, TColumn>
): columnDef is NiraColDef<TData, TColumn> => {
    return "colId" in columnDef;
};

// Custom type guard to determine if a column definition is a ColGroupDef
export const isColGroupDef = <TData, TColumn extends string>(
    columnDef: NiraColDef<TData, TColumn> | NiraColGroupDef<TData, TColumn>
): columnDef is NiraColGroupDef<TData, TColumn> => {
    return "children" in columnDef;
};

export const hideUnwantedColumns = <TData, TColumn extends string>(
    columnDefs: AgGridColumnDefs<TData, TColumn>,
    columnsToHide: readonly TColumn[]
): void => {
    columnDefs.forEach((def) => {
        if (isColGroupDef(def)) {
            // Recursively process children for column groups
            hideUnwantedColumns(def.children, columnsToHide);
        } else if (isColDef(def) && def.colId) {
            def.hide = columnsToHide.includes(def.colId);
        }
    });
};

export const flexLastColumn = <TData, TColumn extends string>(
    columnDefs: AgGridColumnDefs<TData, TColumn>,
    offset = 0
): void => {
    const tailIndex = columnDefs.length - (1 + offset);
    if (tailIndex >= 0) {
        const tail = columnDefs[columnDefs.length - (1 + offset)];
        if (isColGroupDef(tail)) {
            flexLastColumn(tail.children);
        } else {
            if (tail.hide) {
                flexLastColumn(columnDefs, (offset = 1));
            }
            tail.flex = 1;
        }
    }
};

// Columns for the scenarios table on the project page
export type ScenariosTableColumnId =
    | "title"
    | "cost"
    | "costPerMw"
    | "rank"
    | "energyMw"
    | "capacityMw"
    | "hybridLimit"
    | "sensitivities"
    | "projectAdditions"
    | "runStatus"
    | "timing"
    | "inputData"
    | "costAssumptions"
    | "archived"
    | "customUpgrades";

// Columns for the allocated upgrades table
export type AllocatedUpgradesTableColumnId =
    | "projectId"
    | "networkUpgradeId"
    | "networkUpgradeName"
    | "constraintType"
    | "allocatedCost"
    | "maxDfax"
    | "sumMwImpact"
    | "totalCost"
    | "facilityNames"
    | "notes"
    | "percentAllocation";

// Columns for the constraints table
export type ConstraintsTableColumnId =
    | "projectId"
    | "queue"
    | "monitoredFacilityName"
    | "facilityDetails"
    | "constraintType"
    | "dfax"
    | "mwImpact"
    | "preStudyLoad"
    | "postStudyLoad"
    | "preStudyFlow"
    | "postStudyFlow"
    | "rating"
    | "worstContingency"
    | "networkUpgrades"
    | "notes"
    | "model"
    | "area";

// Columns for the scenario projects table
export type ScenarioProjectsTableColumnId =
    | "projectId"
    | "subregion"
    | "numSharedUpgrades"
    | "costPerMW"
    | "rank"
    | "sumAllocatedCost"
    | "erisSize"
    | "nrisSize"
    | "erisThermal"
    | "erisLpc"
    | "erisComplex"
    | "nrisThermal"
    | "nrisComplex"
    | "erisSnu"
    | "assignedUpgrades"
    | "group"
    | "fuelType"
    | "hybridLimit"
    | "jtiq";

export type ClusterProjectsTableColumnId =
    | "projectId"
    | "fuelType"
    | "energySize"
    | "capacitySize"
    | "hybridLimit"
    | "baseCostPerMw"
    | "baseRank";
